import type {
  AiUsage,
  EventDetail,
  EventRecord,
  ExplainResult,
  IngestRunRecord,
  LookupResult,
  SituationBrief,
  StatsResponse,
  TravelBrief,
  ZoneDetail,
  ZoneSummary,
} from '@opensentry/shared';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`);
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`, {
    method: 'POST',
    headers: body ? { 'content-type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

export interface EventsFilter {
  since?: number;
  category?: string;
  severity?: string;
  country?: string;
  limit?: number;
}

export const api = {
  stats: () => get<StatsResponse>('/stats'),
  zones: () => get<ZoneSummary[]>('/zones'),
  zone: (id: number) => get<ZoneDetail>(`/zones/${id}`),
  events: (f: EventsFilter = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v != null) q.set(k, String(v));
    return get<EventRecord[]>(`/events?${q.toString()}`);
  },
  event: (id: number) => get<EventDetail>(`/events/${id}`),
  lookup: (body: { address?: string; lat?: number; lng?: number; radiusKm?: number }) =>
    post<LookupResult>('/lookup', body),
  seedPresets: () => get<Array<{ key: string; label: string }>>('/employees/seed-presets'),
  seed: async (preset?: string) => {
    const q = preset ? `?preset=${encodeURIComponent(preset)}` : '';
    const emp = await post<{ imported: number; zonesCreated: number }>(`/employees/seed${q}`);
    // Populate a curated event set so the demo map is match-rich without
    // waiting on live ingest.
    const ev = await post<{ inserted: number; matched: number; total: number }>(
      '/admin/seed-events',
    );
    return { ...emp, events: ev.inserted, matched: ev.matched };
  },
  importCsv: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/api/employees/import`, { method: 'POST', body: form });
    if (!res.ok) throw new Error(`import → ${res.status}`);
    return res.json() as Promise<{ imported: number; failed: number; zonesCreated: number }>;
  },
  employees: (limit = 200, offset = 0) =>
    get<{ total: number; employees: Array<Record<string, unknown>> }>(
      `/employees?limit=${limit}&offset=${offset}`,
    ),
  runs: () => get<IngestRunRecord[]>('/admin/runs'),
  cursors: () => get<Record<string, string | null>>('/admin/cursors'),
  triggerIngest: (source: string) => post<unknown>(`/admin/ingest/${source}`),
  acknowledgeZone: (zoneId: number) => post<unknown>(`/zones/${zoneId}/acknowledge`),
  feedback: (eventId: number, relevant: boolean, note?: string) =>
    post<unknown>('/feedback', { eventId, relevant, note }),

  // §21 — AI layer
  brief: () => get<{ brief: SituationBrief | null }>('/brief/latest'),
  generateBrief: () =>
    post<{ brief: SituationBrief | null; generated: boolean }>('/brief/generate'),
  explainMatch: (eventId: number, zoneId: number) =>
    get<ExplainResult>(`/events/${eventId}/explain?zoneId=${zoneId}`),
  travelBrief: (body: { address?: string; lat?: number; lng?: number; radiusKm?: number }) =>
    post<TravelBrief>('/lookup/brief', body),
  aiUsage: () => get<AiUsage>('/admin/ai-usage'),
  /** SSE endpoint URL for the per-zone summary stream (§21.6). */
  zoneSummaryUrl: (zoneId: number) => `${BASE}/api/zones/${zoneId}/summary`,
};
